import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { AuthService } from '../../core/services/auth.service';
import { OperationsService } from '../../core/services/operations.service';
import {
  OnboardingOperator,
  OnboardingCompletionRequest,
  OnboardingSummary,
  OperatorOnboardingService,
  RouterOsScriptResult
} from '../../core/services/operator-onboarding.service';

interface VehicleDraft {
  clientReference: string;
  vehicleIdentificationNumber: string;
  vehicleRegistration: string;
  vehicleManufacturerMake: string;
  vehicleManufacturerModel: string;
  vehicleYear: number | null;
  vehicleSeatingCapacity: number | null;
  vehicleLabel: string;
  statusID: number;
}

interface SimDraft {
  clientReference: string;
  deviceSIMNumber: string;
  deviceSIMProvider: string;
  deviceSIMContractType: string;
  statusID: number;
}

interface DeviceAssignmentDraft {
  clientReference: string;
  vehicleMode: 'existing' | 'new';
  vehicleID: number | null;
  vehicleClientReference: string;
  primarySIMMode: 'none' | 'existing' | 'new';
  primarySIMID: number | null;
  primarySIMClientReference: string;
  secondarySIMMode: 'none' | 'existing' | 'new';
  secondarySIMID: number | null;
  secondarySIMClientReference: string;
  deviceModel: string;
  deviceType: string;
  deviceSerialNumber: string;
  deviceImei: string;
  generateRouterOsScript: boolean;
}

@Component({
  selector: 'app-operator-onboarding',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './operator-onboarding.component.html',
  styleUrls: ['./operator-onboarding.component.css']
})
export class OperatorOnboardingComponent implements OnInit {
  private onboardingService = inject(OperatorOnboardingService);
  private operationsService = inject(OperationsService);
  private authService = inject(AuthService);
  private router = inject(Router);

  readonly pendingStatusId = 4;
  readonly steps = [
    { key: 'operator', label: 'Operator', icon: 'badge' },
    { key: 'vehicles', label: 'Vehicles', icon: 'directions_bus' },
    { key: 'sims', label: 'SIM cards', icon: 'sim_card' },
    { key: 'assignments', label: 'Devices', icon: 'router' },
    { key: 'scripts', label: 'Scripts', icon: 'terminal' },
    { key: 'monitor', label: 'Fleet monitor', icon: 'monitor_heart' }
  ];

  operators: OnboardingOperator[] = [];
  selectedOperator: OnboardingOperator | null = null;
  selectedOperatorUserId: number | null = null;
  summary: OnboardingSummary | null = null;
  activeStepIndex = 0;
  searchTerm = '';
  showPendingOnly = true;
  isLoadingOperators = false;
  isLoadingSummary = false;
  isCompleting = false;
  isValidatingFleet = false;
  errorMessage = '';
  successMessage = '';
  completionScripts: RouterOsScriptResult[] = [];
  regeneratedScripts: RouterOsScriptResult[] = [];
  copiedScriptDeviceId: number | null = null;
  fleetRows: any[] = [];
  operatorScopedFleetRows: any[] = [];

  organisationDraft = {
    orgName: '',
    orgRegistrationNumber: '',
    powerlynxLocationID: '',
    operatorCode: '',
    operatorType: '',
    whatsAppNumber: ''
  };
  readonly operatorTypes = ['bus', 'truck', 'taxi'];
  private originalOrganisationDraft = { ...this.organisationDraft };

  vehicles: VehicleDraft[] = [];
  sims: SimDraft[] = [];
  assignments: DeviceAssignmentDraft[] = [];

  ngOnInit(): void {
    if (!this.canAccess()) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.loadOperators();
  }

  loadOperators(): void {
    this.isLoadingOperators = true;
    this.errorMessage = '';
    const search = this.searchTerm.trim();
    const filter = search
      ? { search }
      : this.showPendingOnly
        ? { statusId: this.pendingStatusId }
        : undefined;

    this.onboardingService.getOperators(filter).pipe(
      finalize(() => this.isLoadingOperators = false)
    ).subscribe({
      next: operators => this.operators = operators,
      error: error => {
        console.error('Unable to load onboarding operators:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to load operator onboarding queue.');
      }
    });
  }

  selectOperator(operator: OnboardingOperator): void {
    const operatorUserId = this.getOperatorUserId(operator);

    if (!operatorUserId) {
      this.errorMessage = 'This operator record does not include an operator user ID.';
      return;
    }

    this.selectedOperator = operator;
    this.selectedOperatorUserId = operatorUserId;
    this.loadSummary();
  }

  loadSummary(): void {
    if (!this.selectedOperatorUserId) {
      return;
    }

    this.isLoadingSummary = true;
    this.errorMessage = '';
    this.onboardingService.getSummary(this.selectedOperatorUserId).pipe(
      finalize(() => this.isLoadingSummary = false)
    ).subscribe({
      next: summary => {
        this.summary = summary;
        this.mergeSelectedOperatorSummary(summary);
        this.hydrateOrganisationDraft(summary);
        this.resetDrafts();
        this.activeStepIndex = 0;
      },
      error: error => {
        console.error('Unable to load onboarding summary:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to load onboarding summary for this operator.');
      }
    });
  }

  addVehicle(): void {
    const next = this.vehicles.length + 1;
    this.vehicles.push({
      clientReference: `vehicle-${Date.now()}-${next}`,
      vehicleIdentificationNumber: '',
      vehicleRegistration: '',
      vehicleManufacturerMake: '',
      vehicleManufacturerModel: '',
      vehicleYear: new Date().getFullYear(),
      vehicleSeatingCapacity: null,
      vehicleLabel: '',
      statusID: 1
    });
  }

  removeVehicle(index: number): void {
    const [vehicle] = this.vehicles.splice(index, 1);
    this.assignments.forEach(assignment => {
      if (assignment.vehicleClientReference === vehicle.clientReference) {
        assignment.vehicleClientReference = '';
      }
    });
  }

  addSim(): void {
    const next = this.sims.length + 1;
    this.sims.push({
      clientReference: `sim-${Date.now()}-${next}`,
      deviceSIMNumber: '',
      deviceSIMProvider: '',
      deviceSIMContractType: 'Prepaid',
      statusID: 1
    });
  }

  removeSim(index: number): void {
    const [sim] = this.sims.splice(index, 1);
    this.assignments.forEach(assignment => {
      if (assignment.primarySIMClientReference === sim.clientReference) assignment.primarySIMClientReference = '';
      if (assignment.secondarySIMClientReference === sim.clientReference) assignment.secondarySIMClientReference = '';
    });
  }

  addAssignment(): void {
    this.assignments.push({
      clientReference: `router-${Date.now()}-${this.assignments.length + 1}`,
      vehicleMode: this.hasExistingVehicles() ? 'existing' : 'new',
      vehicleID: null,
      vehicleClientReference: '',
      primarySIMMode: 'none',
      primarySIMID: null,
      primarySIMClientReference: '',
      secondarySIMMode: 'none',
      secondarySIMID: null,
      secondarySIMClientReference: '',
      deviceModel: 'MikroTik LtAP',
      deviceType: 'RouterOS GPS',
      deviceSerialNumber: '',
      deviceImei: '',
      generateRouterOsScript: true
    });
  }

  removeAssignment(index: number): void {
    this.assignments.splice(index, 1);
  }

  submitCompletion(): void {
    if (!this.selectedOperatorUserId) {
      return;
    }

    const validationError = this.getValidationError();
    if (validationError) {
      this.errorMessage = validationError;
      return;
    }

    const payload = this.buildCompletionPayload();
    this.isCompleting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.onboardingService.complete(this.selectedOperatorUserId, payload).pipe(
      finalize(() => this.isCompleting = false)
    ).subscribe({
      next: response => {
        this.completionScripts = response.routerOsScripts || [];
        this.copiedScriptDeviceId = null;
        this.successMessage = 'Operator onboarding completed.';
        this.activeStepIndex = 4;
        this.loadSummaryAfterCompletion();
      },
      error: error => {
        console.error('Unable to complete onboarding:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to complete onboarding. Check the highlighted step and try again.');
      }
    });
  }

  regenerateScript(deviceId: number | null | undefined): void {
    if (!deviceId) {
      this.errorMessage = 'Select an already-linked device before regenerating its RouterOS script.';
      return;
    }

    this.errorMessage = '';
    this.onboardingService.regenerateRouterOsScript(deviceId).subscribe({
      next: script => {
        this.copiedScriptDeviceId = null;
        this.regeneratedScripts = [
          script,
          ...this.regeneratedScripts.filter(existing => Number(existing.deviceID) !== Number(script.deviceID || deviceId))
        ];
        this.successMessage = `Regenerated script for device ${script.deviceID || deviceId}.`;
      },
      error: error => {
        console.error('Unable to regenerate RouterOS script:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to regenerate the RouterOS script for this device.');
      }
    });
  }

  viewScript(deviceId: number | null | undefined): void {
    if (!deviceId) {
      this.errorMessage = 'Select an already-linked device before viewing its RouterOS script.';
      return;
    }

    this.errorMessage = '';
    this.onboardingService.regenerateRouterOsScript(deviceId).subscribe({
      next: script => {
        this.copiedScriptDeviceId = null;
        this.regeneratedScripts = [
          script,
          ...this.regeneratedScripts.filter(existing => Number(existing.deviceID) !== Number(script.deviceID || deviceId))
        ];
        this.successMessage = `Loaded script for device ${script.deviceID || deviceId}.`;
      },
      error: error => {
        console.error('Unable to load RouterOS script:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to load the RouterOS script for this device.');
      }
    });
  }

  async copyScript(script: RouterOsScriptResult): Promise<void> {
    const scriptText = script.routerOsScript || '';
    if (!scriptText) {
      this.errorMessage = 'There is no RouterOS script to copy.';
      return;
    }

    try {
      await this.writeToClipboard(scriptText);
      this.copiedScriptDeviceId = Number(script.deviceID) || null;
      this.errorMessage = '';
      this.successMessage = `Full RouterOS script copied${script.deviceID ? ` for device ${script.deviceID}` : ''}.`;
    } catch (error) {
      console.error('Unable to copy RouterOS script:', error);
      this.copiedScriptDeviceId = null;
      this.errorMessage = 'Unable to copy the script. Select the full script and copy it manually.';
    }
  }

  isScriptCopied(script: RouterOsScriptResult): boolean {
    return !!script.deviceID && this.copiedScriptDeviceId === Number(script.deviceID);
  }

  private async writeToClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      if (!document.execCommand('copy')) {
        throw new Error('Clipboard copy was rejected by the browser.');
      }
    } finally {
      document.body.removeChild(textArea);
    }
  }

  validateEmployeeFleetMonitor(): void {
    const organisationId = this.getSelectedOrganisationId();

    if (!organisationId) {
      this.errorMessage = 'The selected operator summary does not include an organisation ID for fleet monitor validation.';
      return;
    }

    this.isValidatingFleet = true;
    this.errorMessage = '';
    this.operationsService.getFleetHeartbeat(organisationId).pipe(
      finalize(() => this.isValidatingFleet = false)
    ).subscribe({
      next: rows => {
        this.fleetRows = rows;
        this.activeStepIndex = 5;
      },
      error: error => {
        console.error('Unable to validate employee fleet monitor:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to load fleet monitor rows for this organisation.');
      }
    });
  }

  validateOperatorScopedFleetMonitor(): void {
    this.isValidatingFleet = true;
    this.errorMessage = '';
    this.operationsService.getFleetHeartbeat().pipe(
      finalize(() => this.isValidatingFleet = false)
    ).subscribe({
      next: rows => {
        this.operatorScopedFleetRows = rows;
        this.activeStepIndex = 5;
      },
      error: error => {
        console.error('Unable to validate operator-scoped fleet monitor:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to load operator-scoped fleet monitor rows.');
      }
    });
  }

  nextStep(): void {
    this.activeStepIndex = Math.min(this.activeStepIndex + 1, this.steps.length - 1);
  }

  previousStep(): void {
    this.activeStepIndex = Math.max(this.activeStepIndex - 1, 0);
  }

  goToStep(index: number): void {
    this.activeStepIndex = index;
  }

  getOperatorUserId(operator: OnboardingOperator | null): number | null {
    return Number(operator?.operatorUserId || operator?.operatorUserID || operator?.userID || operator?.id || 0) || null;
  }

  getOperatorName(operator: OnboardingOperator | null = this.selectedOperator): string {
    return operator?.orgName || operator?.organisationName || operator?.userName || operator?.username || operator?.email || 'Unnamed operator';
  }

  getStatusLabel(operator: OnboardingOperator): string {
    const statusName = String(this.pickFirst(operator.statusName, (operator as any).userStatusName, (operator as any).organisationStatusName) || '').trim();

    if (statusName) {
      return statusName;
    }

    if (this.isSelectedOperator(operator) && this.summary?.isReadyForFleetMonitor) {
      return 'Ready';
    }

    const statusId = this.getOperatorStatusId(operator);

    if (statusId === 1) {
      return 'Active';
    }

    if (statusId === 2) {
      return 'Inactive';
    }

    if (statusId === 3) {
      return 'Suspended';
    }

    if (statusId === 4) {
      return 'Pending';
    }

    if (statusId === 5) {
      return 'Confirmed';
    }

    if (statusId === 6) {
      return 'Cancelled';
    }

    return this.isSelectedOperator(operator) && this.summary ? 'Ready' : 'Unknown';
  }

  getOutstandingSteps(): string[] {
    return this.summary?.outstandingSteps?.length ? this.summary.outstandingSteps : ['No outstanding steps reported.'];
  }

  hasExistingVehicles(): boolean {
    return Boolean(this.summary?.vehicles?.length);
  }

  hasExistingAvailableSims(): boolean {
    return Boolean(this.summary?.availableDeviceSIMs?.length);
  }

  isExistingSimAlreadySelected(simId: number | null | undefined, assignmentIndex: number, slot: 'primary' | 'secondary'): boolean {
    if (!simId) {
      return false;
    }

    return this.assignments.some((assignment, index) => {
      const primaryMatch = assignment.primarySIMMode === 'existing' && Number(assignment.primarySIMID) === Number(simId);
      const secondaryMatch = assignment.secondarySIMMode === 'existing' && Number(assignment.secondarySIMID) === Number(simId);
      return index !== assignmentIndex ? primaryMatch || secondaryMatch : slot === 'primary' ? secondaryMatch : primaryMatch;
    });
  }

  getVehicleLabelByAssignment(assignment: DeviceAssignmentDraft): string {
    if (assignment.vehicleMode === 'existing') {
      const vehicle = this.summary?.vehicles?.find(item => Number(item.vehicleID) === Number(assignment.vehicleID));
      return this.formatVehicle(vehicle);
    }

    const vehicle = this.vehicles.find(item => item.clientReference === assignment.vehicleClientReference);
    return vehicle ? `${vehicle.vehicleRegistration || 'New vehicle'} (${vehicle.clientReference})` : 'No vehicle selected';
  }

  getSimLabelByAssignment(assignment: DeviceAssignmentDraft, slot: 'primary' | 'secondary'): string {
    const mode = slot === 'primary' ? assignment.primarySIMMode : assignment.secondarySIMMode;
    const simID = slot === 'primary' ? assignment.primarySIMID : assignment.secondarySIMID;
    const clientReference = slot === 'primary' ? assignment.primarySIMClientReference : assignment.secondarySIMClientReference;
    if (mode === 'none') return 'Assign SIM later';
    if (mode === 'existing') {
      const sim = this.summary?.availableDeviceSIMs?.find(item => Number(item.deviceSIMID) === Number(simID));
      return this.formatSim(sim);
    }

    const sim = this.sims.find(item => item.clientReference === clientReference);
    return sim ? `${sim.deviceSIMNumber || 'New SIM'} (${sim.clientReference})` : 'No SIM selected';
  }

  hasPrimarySIM(assignment: DeviceAssignmentDraft): boolean {
    return assignment.primarySIMMode === 'existing'
      ? Boolean(assignment.primarySIMID)
      : assignment.primarySIMMode === 'new' ? Boolean(assignment.primarySIMClientReference) : false;
  }

  onPrimarySIMModeChange(assignment: DeviceAssignmentDraft): void {
    assignment.primarySIMID = null;
    assignment.primarySIMClientReference = '';
    assignment.secondarySIMMode = 'none';
    assignment.secondarySIMID = null;
    assignment.secondarySIMClientReference = '';
  }

  onSecondarySIMModeChange(assignment: DeviceAssignmentDraft): void {
    assignment.secondarySIMID = null;
    assignment.secondarySIMClientReference = '';
  }

  formatVehicle(vehicle: any): string {
    if (!vehicle) {
      return 'Unknown vehicle';
    }

    return [vehicle.vehicleRegistration, vehicle.vehicleLabel, vehicle.vehicleIdentificationNumber]
      .filter(Boolean)
      .join(' / ') || `Vehicle ${vehicle.vehicleID}`;
  }

  formatSim(sim: any): string {
    if (!sim) {
      return 'Unknown SIM';
    }

    return [sim.deviceSIMNumber || sim.simNumber, sim.deviceSIMProvider || sim.simProvider]
      .filter(Boolean)
      .join(' / ') || `SIM ${sim.deviceSIMID}`;
  }

  formatDevice(device: any): string {
    return [device?.deviceSerialNumber, device?.deviceModel, device?.deviceImei]
      .filter(Boolean)
      .join(' / ') || `Device ${this.getDeviceId(device) || '-'}`;
  }

  getDeviceId(device: any): number | null {
    return Number(device?.deviceID || device?.deviceId || device?.id || 0) || null;
  }

  getDeviceVehicleId(device: any): number | null {
    return Number(device?.vehicleID || device?.vehicleId || device?.vehicle?.vehicleID || device?.vehicle?.vehicleId || 0) || null;
  }

  getScriptRows(): RouterOsScriptResult[] {
    const byDevice = new Map<number | string, RouterOsScriptResult>();
    [...this.completionScripts, ...this.regeneratedScripts].forEach(script => {
      byDevice.set(script.deviceID || `${script.deviceSerialNumber}-${script.vehicleID}`, script);
    });
    return Array.from(byDevice.values());
  }

  getHeartbeatStatus(row: any): string {
    return row.status || row.vehicleStatus || row.trackingStatus || row.heartbeatStatus || 'NeverSeen';
  }

  private canAccess(): boolean {
    const user = this.authService.getCurrentUser();
    const role = normalizeUserRole(user?.userRoleName || user?.userRole || user?.userRoleID);
    return role === UserRole.ADMIN || role === UserRole.PLATFORM_EMPLOYEE;
  }

  private isSelectedOperator(operator: OnboardingOperator): boolean {
    return Boolean(this.selectedOperatorUserId && this.getOperatorUserId(operator) === this.selectedOperatorUserId);
  }

  private getOperatorStatusId(operator: OnboardingOperator | any): number | null {
    return Number(
      operator?.statusID
      || operator?.statusId
      || operator?.userStatusID
      || operator?.userStatusId
      || operator?.organisationStatusID
      || operator?.organisationStatusId
      || operator?.organisation?.statusID
      || operator?.organisation?.statusId
      || 0
    ) || null;
  }

  private pickFirst(...values: any[]): string {
    const match = values.find(value => String(value ?? '').trim().length > 0);
    return match === undefined || match === null ? '' : String(match).trim();
  }

  private hydrateOrganisationDraft(summary: OnboardingSummary): void {
    const operator = summary.operator || {};
    const organisation = operator.organisation || (summary as any).organisation || {};
    const selectedOrganisation = (this.selectedOperator as any)?.organisation || {};
    this.organisationDraft = {
      orgName: this.pickFirst(
        organisation.orgName,
        organisation.organisationName,
        operator.orgName,
        operator.organisationName,
        this.selectedOperator?.orgName,
        this.selectedOperator?.organisationName
      ),
      orgRegistrationNumber: this.pickFirst(
        organisation.orgRegistrationNumber,
        organisation.organisationRegistrationNumber,
        organisation.registrationNumber,
        operator.orgRegistrationNumber,
        operator.organisationRegistrationNumber,
        (operator as any).registrationNumber,
        (this.selectedOperator as any)?.orgRegistrationNumber,
        (this.selectedOperator as any)?.organisationRegistrationNumber,
        selectedOrganisation.orgRegistrationNumber,
        selectedOrganisation.organisationRegistrationNumber
      ),
      operatorCode: this.pickFirst(
        organisation.operatorCode,
        operator.operatorCode,
        (this.selectedOperator as any)?.operatorCode,
        selectedOrganisation.operatorCode
      ),
      operatorType: this.pickFirst(
        organisation.operatorType,
        operator.operatorType,
        (this.selectedOperator as any)?.operatorType,
        selectedOrganisation.operatorType
      ),
      powerlynxLocationID: this.pickFirst(
        organisation.powerlynxLocationID,
        organisation.powerlynxLocationId,
        operator.powerlynxLocationID,
        operator.powerlynxLocationId,
        (this.selectedOperator as any)?.powerlynxLocationID,
        (this.selectedOperator as any)?.powerlynxLocationId,
        selectedOrganisation.powerlynxLocationID,
        selectedOrganisation.powerlynxLocationId
      ),
      whatsAppNumber: this.pickFirst(
        organisation.whatsAppNumber,
        organisation.whatsappNumber,
        organisation.orgWhatsAppNumber,
        organisation.organisationWhatsAppNumber,
        organisation.organisationPhone,
        operator.whatsAppNumber,
        operator.whatsappNumber,
        operator.operatorWhatsAppNumber,
        operator.phoneNumber,
        (this.selectedOperator as any)?.whatsAppNumber,
        (this.selectedOperator as any)?.whatsappNumber,
        (this.selectedOperator as any)?.operatorWhatsAppNumber,
        (this.selectedOperator as any)?.phoneNumber,
        selectedOrganisation.whatsAppNumber,
        selectedOrganisation.whatsappNumber
      )
    };
    this.originalOrganisationDraft = { ...this.organisationDraft };
  }

  private mergeSelectedOperatorSummary(summary: OnboardingSummary): void {
    if (!this.selectedOperator || !summary.operator) {
      return;
    }

    const organisation = summary.operator.organisation || (summary as any).organisation || {};
    const mergedOperator: OnboardingOperator = {
      ...this.selectedOperator,
      ...summary.operator,
      orgName: this.pickFirst(organisation.orgName, organisation.organisationName, summary.operator.orgName, summary.operator.organisationName, this.selectedOperator.orgName),
      organisationName: this.pickFirst(organisation.organisationName, organisation.orgName, summary.operator.organisationName, summary.operator.orgName, this.selectedOperator.organisationName),
      statusID: this.getOperatorStatusId(summary.operator as OnboardingOperator) || this.getOperatorStatusId(this.selectedOperator) || this.selectedOperator.statusID,
      statusName: this.pickFirst(summary.operator.statusName, (summary.operator as any).userStatusName, (summary.operator as any).organisationStatusName, this.selectedOperator.statusName)
    };

    this.selectedOperator = mergedOperator;
    this.operators = this.operators.map(operator =>
      this.getOperatorUserId(operator) === this.selectedOperatorUserId ? mergedOperator : operator
    );
  }

  private resetDrafts(): void {
    this.vehicles = [];
    this.sims = [];
    this.assignments = [];
    this.completionScripts = [];
    this.regeneratedScripts = [];
    this.fleetRows = [];
    this.operatorScopedFleetRows = [];

    if (!this.hasExistingVehicles()) {
      this.addVehicle();
    }

    if (!this.hasExistingAvailableSims()) {
      this.addSim();
    }

    this.addAssignment();
  }

  private loadSummaryAfterCompletion(): void {
    if (!this.selectedOperatorUserId) {
      return;
    }

    this.onboardingService.getSummary(this.selectedOperatorUserId).subscribe({
      next: summary => {
        this.summary = summary;
        this.mergeSelectedOperatorSummary(summary);
        this.hydrateOrganisationDraft(summary);
      },
      error: error => console.error('Unable to refresh onboarding summary:', error)
    });
  }

  private buildCompletionPayload(): OnboardingCompletionRequest {
    const payload: OnboardingCompletionRequest = {
      activateOperator: true,
      activateOrganisation: true,
      deviceAssignments: []
    };

    const organisation = this.buildOrganisationPayload();
    if (Object.keys(organisation).length) {
      payload.organisation = organisation;
    }

    const vehicles = this.vehicles
      .filter(vehicle => this.isVehicleUsed(vehicle.clientReference))
      .map(vehicle => ({
        clientReference: vehicle.clientReference,
        vehicleIdentificationNumber: vehicle.vehicleIdentificationNumber,
        vehicleRegistration: vehicle.vehicleRegistration,
        vehicleManufacturerMake: vehicle.vehicleManufacturerMake,
        vehicleManufacturerModel: vehicle.vehicleManufacturerModel,
        vehicleYear: Number(vehicle.vehicleYear),
        vehicleSeatingCapacity: Number(vehicle.vehicleSeatingCapacity),
        vehicleLabel: vehicle.vehicleLabel,
        statusID: Number(vehicle.statusID || 1)
      }));

    if (vehicles.length) {
      payload.vehicles = vehicles;
    }

    const deviceSIMs = this.sims
      .filter(sim => this.isSimUsed(sim.clientReference))
      .map(sim => ({
        clientReference: sim.clientReference,
        deviceSIMNumber: sim.deviceSIMNumber,
        deviceSIMProvider: sim.deviceSIMProvider,
        deviceSIMContractType: sim.deviceSIMContractType,
        statusID: Number(sim.statusID || 1)
      }));

    if (deviceSIMs.length) {
      payload.deviceSIMs = deviceSIMs;
    }

    payload.deviceAssignments = this.assignments.map(assignment => {
      const row: any = {
        clientReference: assignment.clientReference,
        deviceModel: assignment.deviceModel,
        deviceType: assignment.deviceType,
        deviceSerialNumber: assignment.deviceSerialNumber,
        deviceImei: assignment.deviceImei,
        generateRouterOsScript: assignment.generateRouterOsScript
      };

      if (assignment.vehicleMode === 'existing') {
        row.vehicleID = Number(assignment.vehicleID);
      } else {
        row.vehicleClientReference = assignment.vehicleClientReference;
      }

      if (assignment.primarySIMMode === 'existing') row.primarySIMID = Number(assignment.primarySIMID);
      if (assignment.primarySIMMode === 'new') row.primarySIMClientReference = assignment.primarySIMClientReference;
      if (assignment.secondarySIMMode === 'existing') row.secondarySIMID = Number(assignment.secondarySIMID);
      if (assignment.secondarySIMMode === 'new') row.secondarySIMClientReference = assignment.secondarySIMClientReference;

      return row;
    });

    return payload;
  }

  private buildOrganisationPayload(): Record<string, string | number> {
    const payload: Record<string, string | number> = {};

    (Object.keys(this.organisationDraft) as Array<keyof typeof this.organisationDraft>).forEach(key => {
      const value = String(this.organisationDraft[key] || '').trim();
      const original = String(this.originalOrganisationDraft[key] || '').trim();

      if (value && value !== original) {
        payload[key] = key === 'powerlynxLocationID'
          ? Number(value)
          : key === 'operatorType'
            ? value.toLowerCase().replace(/[\s-]+/g, '_')
            : value;
      }
    });

    return payload;
  }

  private getValidationError(): string {
    const powerlynxLocationID = String(this.organisationDraft.powerlynxLocationID || '').trim();
    if (powerlynxLocationID && (!/^[1-9]\d*$/.test(powerlynxLocationID) || !Number.isFinite(Number(powerlynxLocationID)))) {
      return 'Enter a positive whole number for the Powerlynx Location ID.';
    }

    if (!this.assignments.length) {
      return 'Add at least one device assignment before completing onboarding.';
    }

    const usedExistingSims = new Set<number>();
    const usedNewSims = new Set<string>();

    for (const assignment of this.assignments) {
      if (assignment.vehicleMode === 'existing' && !assignment.vehicleID) {
        return 'Choose an existing vehicle for every existing-vehicle assignment.';
      }

      if (assignment.vehicleMode === 'new' && !assignment.vehicleClientReference) {
        return 'Choose a new vehicle for every new-vehicle assignment.';
      }

      if (assignment.secondarySIMMode !== 'none' && !this.hasPrimarySIM(assignment)) {
        return 'Choose a primary SIM before assigning a secondary SIM.';
      }

      for (const slot of ['primary', 'secondary'] as const) {
        const mode = slot === 'primary' ? assignment.primarySIMMode : assignment.secondarySIMMode;
        const simID = slot === 'primary' ? assignment.primarySIMID : assignment.secondarySIMID;
        const clientReference = slot === 'primary' ? assignment.primarySIMClientReference : assignment.secondarySIMClientReference;
        if (mode === 'existing') {
          if (!simID) return `Choose an available ${slot} SIM.`;
          if (usedExistingSims.has(Number(simID))) return 'The same existing SIM cannot be reused in this submission.';
          usedExistingSims.add(Number(simID));
        }
        if (mode === 'new') {
          if (!clientReference) return `Choose a new ${slot} SIM.`;
          if (usedNewSims.has(clientReference)) return 'The same new SIM cannot be reused in this submission.';
          usedNewSims.add(clientReference);
        }
      }

      if (!assignment.deviceSerialNumber.trim() || !assignment.deviceImei.trim()) {
        return 'Enter a serial number and IMEI for every device assignment.';
      }
    }

    const usedVehicleRefs = new Set(
      this.assignments
        .filter(assignment => assignment.vehicleMode === 'new')
        .map(assignment => assignment.vehicleClientReference)
    );

    for (const vehicle of this.vehicles.filter(item => usedVehicleRefs.has(item.clientReference))) {
      if (!vehicle.vehicleRegistration.trim() || !vehicle.vehicleIdentificationNumber.trim()) {
        return 'Enter registration and identification number for every new vehicle used in an assignment.';
      }
    }

    const usedSimRefs = new Set(
      this.assignments
        .flatMap(assignment => [
          assignment.primarySIMMode === 'new' ? assignment.primarySIMClientReference : '',
          assignment.secondarySIMMode === 'new' ? assignment.secondarySIMClientReference : ''
        ])
        .filter(Boolean)
    );

    for (const sim of this.sims.filter(item => usedSimRefs.has(item.clientReference))) {
      if (!sim.deviceSIMNumber.trim() || !sim.deviceSIMProvider.trim()) {
        return 'Enter SIM number and provider for every new SIM used in an assignment.';
      }
    }

    return '';
  }

  private isVehicleUsed(clientReference: string): boolean {
    return this.assignments.some(assignment =>
      assignment.vehicleMode === 'new' && assignment.vehicleClientReference === clientReference
    );
  }

  private isSimUsed(clientReference: string): boolean {
    return this.assignments.some(assignment =>
      (assignment.primarySIMMode === 'new' && assignment.primarySIMClientReference === clientReference)
      || (assignment.secondarySIMMode === 'new' && assignment.secondarySIMClientReference === clientReference)
    );
  }

  private getSelectedOrganisationId(): number | null {
    const source = this.summary?.operator?.organisation || this.summary?.operator || this.selectedOperator || {};
    return Number(source.organisationID || source.organisationId || this.selectedOperator?.organisationID || this.selectedOperator?.organisationId || 0) || null;
  }

  private getErrorMessage(error: any, fallback: string): string {
    const payload = error?.error;

    if (typeof payload === 'string') {
      return payload;
    }

    if (payload?.message) {
      return payload.message;
    }

    if (payload?.title) {
      return payload.title;
    }

    if (payload?.errors) {
      return Object.values(payload.errors).flat().join(' ');
    }

    return fallback;
  }
}
